import React, { useEffect, useState, useRef } from 'react'
import { useConfiguratorContext } from '@augmatica/iframe-interface-react'

import { ToggleButtonGroup, ToggleButton, Button } from '@mui/material'
import * as Styled from './styles'
import { ASSEMBLY_ID } from '../../constants/constants'
import { useAppSelector } from '../../hooks'

interface Props {
  disabled?: boolean
}

export const ContructionPlanPanel: React.FC<Props> = ({ disabled }) => {
  const configuratorRef = useRef(useConfiguratorContext())

  const { content } = useAppSelector((state) => state.init) || {}
  const { props } = useAppSelector((state) => state.configResult) || {}
  const { loftLeft, loftRight } = props ?? {}

  const [numberOfLofts, setNumberOfLofts] = useState(loftLeft.value && loftRight.value ? 'two' : 'one')

  useEffect(() => {
    if (disabled) return
    if (numberOfLofts === 'one' && loftLeft.value && loftRight.value) {
      configuratorRef.current.updateItem(ASSEMBLY_ID, ASSEMBLY_ID, {
        loftRight: 0,
      })
      return
    }

    if (numberOfLofts === 'two' && (loftLeft.value === 0 || loftRight.value === 0)) {
      const loftLeftOptions = loftLeft.options.filter((x: any) => x.value > 0)
      const loftRightOptions = loftRight.options.filter((x: any) => x.value > 0)
      configuratorRef.current.updateItem(ASSEMBLY_ID, ASSEMBLY_ID, {
        loftLeft: loftLeft.value || loftLeftOptions.find((x: any) => x.available)?.value || loftLeftOptions[0]?.value,
        loftRight:
          loftRight.value || loftRightOptions.find((x: any) => x.available)?.value || loftLeftOptions[0]?.value,
      })
    }
  }, [disabled, numberOfLofts, loftLeft, loftRight])

  return (
    <Styled.ConstructionPanel>
      {/* Set number of lofts */}
      <ToggleButtonGroup
        color="primary"
        value={numberOfLofts}
        exclusive
        onChange={(event: any) => {
          setNumberOfLofts(event.target.value)
        }}
        aria-label="number-of-lofts"
        disabled={disabled}
      >
        <ToggleButton value="one">
          {numberOfLofts === 'one' && <Styled.Checkmark className="fas fa-check" />}
          {content?.props?.loft?.constructionPlan?.numberOfLofts?.one}
        </ToggleButton>
        <ToggleButton value="two">
          {numberOfLofts === 'two' && <Styled.Checkmark className="fas fa-check" />}
          {content?.props?.loft?.constructionPlan?.numberOfLofts?.two}
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Set left or right */}
      {numberOfLofts === 'one' && (
        <>
          <Styled.SelectionHeader>{content?.props?.loft?.constructionPlan?.positioning?.lable}</Styled.SelectionHeader>

          <Styled.ButtonContainer>
            <Button
              disabled={disabled}
              variant={disabled || loftLeft.value ? 'contained' : 'outlined'}
              onClick={() => {
                if (loftLeft.value) return
                configuratorRef.current.updateItem(ASSEMBLY_ID, ASSEMBLY_ID, {
                  loftLeft: loftRight.value,
                  loftRight: 0,
                })
              }}
            >
              {(disabled || loftLeft.value > 0) && <Styled.Checkmark className="fas fa-check" />}
              {content?.props?.loft?.constructionPlan?.positioning?.left}
            </Button>
            <Button
              disabled={disabled}
              variant={loftRight.value ? 'contained' : 'outlined'}
              onClick={() => {
                if (loftRight.value) return
                configuratorRef.current.updateItem(ASSEMBLY_ID, ASSEMBLY_ID, {
                  loftRight: loftLeft.value,
                  loftLeft: 0,
                })
              }}
            >
              {loftRight.value > 0 && <Styled.Checkmark className="fas fa-check" />}
              {content?.props?.loft?.constructionPlan?.positioning?.right}
            </Button>
          </Styled.ButtonContainer>
        </>
      )}

      {loftLeft.value > 0 && (
        <>
          <Styled.SelectionHeader>
            {numberOfLofts === 'two'
              ? `${content?.props.loft.constructionPlan.size}, ${content?.props.loft.constructionPlan.loftOne}`
              : content?.props.loft.constructionPlan.size}
          </Styled.SelectionHeader>
          <Styled.ButtonContainer>
            {loftLeft?.options?.map((option: any, index: any) => {
              if (option.value) {
                const selected = loftLeft.value === option.value
                return (
                  <Button
                    disabled={disabled || option.available === false}
                    onClick={() => {
                      if (selected) return
                      configuratorRef.current.updateItem(ASSEMBLY_ID, ASSEMBLY_ID, {
                        loftLeft: option.value,
                      })
                    }}
                    key={option.value + index}
                    variant={selected ? 'contained' : 'outlined'}
                  >
                    {selected && <Styled.Checkmark className="fas fa-check" />}
                    {/* TODO lös detta på något bättre sätt, kanske en formatter-helper som tar emot värde och locale */}
                    {`${option.value.toString().replace('.', ',')} m²`}
                  </Button>
                )
              }
            })}
          </Styled.ButtonContainer>
        </>
      )}

      {(disabled || loftRight.value > 0) && (
        <>
          <Styled.SelectionHeader>
            {numberOfLofts === 'two'
              ? `${content?.props.loft.constructionPlan.size}, ${content?.props.loft.constructionPlan.loftTwo}`
              : content?.props.loft.constructionPlan.size}
          </Styled.SelectionHeader>
          <Styled.ButtonContainer>
            {loftRight?.options?.map((option: any, index: any) => {
              if (option.value) {
                const selected = loftRight.value === option.value || (disabled && index === 0)
                return (
                  <Button
                    disabled={disabled || option.available === false}
                    onClick={() => {
                      if (selected) return
                      configuratorRef.current.updateItem(ASSEMBLY_ID, ASSEMBLY_ID, {
                        loftRight: option.value,
                      })
                    }}
                    key={option.value + index}
                    variant={selected ? 'contained' : 'outlined'}
                  >
                    {selected && <Styled.Checkmark className="fas fa-check" />}
                    {`${option.value.toString().replace('.', ',')} m²`}
                  </Button>
                )
              }
            })}
          </Styled.ButtonContainer>
        </>
      )}
    </Styled.ConstructionPanel>
  )
}

export default ContructionPlanPanel
